const STORAGE_KEY = 'Settings';

const SCHEMA = {
	speed: {
		name: 'Speed',
		type: 'select',
		defaultValue: 1.0,
		sticky: true,
		values: [
			[0.5, 'Very Fast'],
			[0.75, 'Fast'],
			[1.0, 'Normal'],
			[1.25, 'Slow'],
			[1.5, 'Very Slow'],
		],
	},
	scanLines: {
		name: 'Scan Lines',
		type: 'checkbox',
		defaultValue: false,
		sticky: true,
	},
	scanLineMode: {
		name: 'Scan Line Style',
		type: 'select',
		defaultValue: 'auto',
		sticky: true,
		values: [
			['auto', 'Auto (Adaptive)'],
			['thin', 'Thin (1x)'],
			['medium', 'Medium (2x)'],
			['thick', 'Thick (3x)'],
		],
	},
	units: {
		name: 'Units',
		type: 'select',
		defaultValue: 'us',
		sticky: true,
		values: [
			['us', 'US'],
			['si', 'Metric'],
		],
	},
	refreshTime: {
		name: 'Refresh',
		type: 'select',
		defaultValue: 600_000,
		sticky: true,
		values: [
			[30_000, 'TESTING'],
			[300_000, '5 minutes'],
			[600_000, '10 minutes'],
			[900_000, '15 minutes'],
			[1_800_000, '30 minutes'],
		],
	},
	mediaPlaying: {
		name: 'Media Playing',
		type: 'boolean',
		defaultValue: false,
		sticky: true,
	},
	mediaVolume: {
		name: 'Volume',
		type: 'number',
		defaultValue: 0.75,
		sticky: true,
	},
	customText: {
		name: 'Custom Text',
		type: 'string',
		defaultValue: '',
		sticky: true,
	},
	customTextEnable: {
		name: 'Enable Custom Text',
		type: 'checkbox',
		defaultValue: false,
		sticky: true,
	},
	stingersEnabled: {
		name: 'Display Stingers',
		type: 'checkbox',
		defaultValue: false,
		sticky: true,
	},
};

const applyScanLines = (value) => {
	const container = document.getElementById('container');
	if (!container) return;
	if (value) {
		container.classList.add('scanlines');
	} else {
		container.classList.remove('scanlines', 'scanlines-auto', 'scanlines-fine', 'scanlines-normal', 'scanlines-thick', 'scanlines-classic', 'scanlines-retro');
	}
	document.dispatchEvent(new CustomEvent('scanlines-changed', { detail: { enabled: !!value } }));
	if (value && typeof window.applyScanlineScaling === 'function') {
		window.applyScanlineScaling(window.currentScale ?? 1.0);
	}
};

const applyScanLineMode = (scanLinesEnabled) => {
	if (scanLinesEnabled && typeof window.applyScanlineScaling === 'function') {
		window.applyScanlineScaling(window.currentScale ?? 1.0);
	}
};

class SettingsStore {
	constructor() {
		this.vals = {};
		this.callbacks = {};
		this.schema = SCHEMA;

		// Load from localStorage
		let stored = {};
		try {
			stored = JSON.parse(localStorage?.getItem(STORAGE_KEY) ?? '{}');
		} catch {
			localStorage?.removeItem(STORAGE_KEY);
		}

		// Parse URL params — same key format as old Setting class: settings-${key}-${type}
		const urlParams = new URLSearchParams(window.location.search);
		const urlValues = {};
		urlParams.forEach((val, key) => {
			const match = key.match(/^settings-(\w+)-(select|checkbox|string)$/);
			if (!match) return;
			const [, name, type] = match;
			if (type === 'select') {
				const num = parseFloat(val);
				urlValues[name] = Number.isNaN(num) ? val : num;
			} else if (type === 'checkbox') {
				urlValues[name] = val === 'true';
			} else {
				urlValues[name] = val;
			}
		});

		// Initialize: default → stored → URL param (URL wins)
		Object.entries(SCHEMA).forEach(([key, def]) => {
			const fromStored = def.sticky && stored[key] !== undefined ? stored[key] : undefined;
			const fromUrl = urlValues[key] !== undefined ? urlValues[key] : undefined;
			this.vals[key] = fromUrl ?? fromStored ?? def.defaultValue;
			this.callbacks[key] = [];
		});

		// Apply always-on layout + initial side effects after DOM is ready
		const applyInit = () => {
			document.querySelector('body')?.classList.add('kiosk');
			document.querySelector('#divTwc')?.classList.add('wide', 'no-cursor');
			applyScanLines(this.vals.scanLines);
			applyScanLineMode(this.vals.scanLines);
			window.dispatchEvent(new Event('resize'));
		};

		if (document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', applyInit);
		} else {
			applyInit();
		}
	}

	get(key) {
		return this.vals[key];
	}

	set(key, value) {
		if (!(key in SCHEMA)) throw new Error(`Unknown setting: ${key}`);
		const def = SCHEMA[key];
		if (def.type === 'select' && !def.values.some(([v]) => v === value)) {
			throw new Error(`Invalid value for ${key}: ${value}`);
		}
		this.vals[key] = value;
		if (SCHEMA[key].sticky) {
			const toStore = Object.fromEntries(
				Object.entries(this.vals).filter(([k]) => SCHEMA[k].sticky),
			);
			localStorage?.setItem(STORAGE_KEY, JSON.stringify(toStore));
		}
		this.callbacks[key].forEach((cb) => cb(value));
		this.applyEffect(key, value);
	}

	on(key, callback) {
		if (!(key in SCHEMA)) throw new Error(`Unknown setting: ${key}`);
		this.callbacks[key].push(callback);
		return () => {
			this.callbacks[key] = this.callbacks[key].filter((cb) => cb !== callback);
		};
	}

	applyEffect(key, value) {
		switch (key) {
			case 'scanLines': applyScanLines(value); break;
			case 'scanLineMode': applyScanLineMode(this.vals.scanLines); break;
			// Persist happens before reload (set() calls this after localStorage write)
			case 'units': window.location.reload(); break;
			default: break;
		}
	}
}

const settings = new SettingsStore();

// Global helper for remote debugging
window.changeScanlineMode = (mode) => {
	const validModes = SCHEMA.scanLineMode.values.map(([v]) => v);
	if (!validModes.includes(mode)) return false;
	settings.set('scanLineMode', mode);
	return true;
};

export default settings;
